import React, { useEffect, useId, useRef, useState } from 'react';
import Modal from './Modal';

const getFileName = (file) => file?.name || file?.file_name || 'selected document';

/**
 * Collects the options for a user-requested PDF conversion. The conversion
 * itself intentionally lives in the caller so this modal can be reused by
 * other document surfaces without owning API or refresh behavior.
 */
const PdfConversionOptionsModal = ({
    file,
    initialOcr = false,
    onClose,
    onConfirm
}) => {
    const [ocrPdf, setOcrPdf] = useState(Boolean(initialOcr));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const formId = useId();

    useEffect(() => {
        setOcrPdf(Boolean(initialOcr));
    }, [file, initialOcr]);

    if (!file) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submittingRef.current) return;

        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            const confirmation = onConfirm?.({ ocrPdf });
            if (confirmation && typeof confirmation.then === 'function') {
                await confirmation;
            }
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submittingRef.current) onClose?.();
    };

    return (
        <Modal
            title="Convert to PDF"
            instructions={`Choose how CaseDB should convert "${getFileName(file)}".`}
            onClose={handleClose}
            footer={
                <div className="modal-footer-actions">
                    <button type="button" className="action alt" onClick={handleClose} disabled={isSubmitting}>Cancel</button>
                    <button type="submit" className="action" form={formId} disabled={isSubmitting}>
                        {isSubmitting ? 'Converting…' : 'Convert'}
                    </button>
                </div>
            }
        >
            <div className="modal-content-wrapper pdf-conversion-options-modal" aria-busy={isSubmitting}>
                <form id={formId} onSubmit={handleSubmit}>
                    <label className="pdf-conversion-ocr-option">
                        <input
                            type="checkbox"
                            checked={ocrPdf}
                            disabled={isSubmitting}
                            onChange={(event) => setOcrPdf(event.target.checked)}
                        />
                        <span>
                            <strong>Make the PDF searchable (OCR)</strong>
                            <small>
                                Recognize text in scans and images. This can take longer than a standard conversion.
                            </small>
                        </span>
                    </label>
                </form>
            </div>
        </Modal>
    );
};

export default PdfConversionOptionsModal;
